import pick from 'lodash/pick';
import { storableError } from '../../util/errors';

// Get API base URL that works for both SSR and client-side
const getApiBaseUrl = () => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, use the dev API server port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // On server-side (SSR), use the marketplace root URL from env
  if (typeof window === 'undefined') {
    const rootUrl = process.env.REACT_APP_MARKETPLACE_ROOT_URL;
    return rootUrl ? rootUrl.replace(/\/$/, '') : '';
  }

  // On client-side, use the current origin
  return window.location.origin;
};

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/CertificatePage/SET_INITIAL_VALUES';

export const FETCH_CERTIFICATE_REQUEST = 'app/CertificatePage/FETCH_CERTIFICATE_REQUEST';
export const FETCH_CERTIFICATE_SUCCESS = 'app/CertificatePage/FETCH_CERTIFICATE_SUCCESS';
export const FETCH_CERTIFICATE_ERROR = 'app/CertificatePage/FETCH_CERTIFICATE_ERROR';

// ================ Reducer ================ //

const initialState = {
  fetchInProgress: false,
  fetchError: null,
  certificateData: null,
  initialized: false,  // Track if data fetch has completed (for SSR hydration)
};

export default function certificatePageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case FETCH_CERTIFICATE_REQUEST:
      return { ...state, fetchInProgress: true, fetchError: null };
    case FETCH_CERTIFICATE_SUCCESS:
      return { ...state, fetchInProgress: false, certificateData: payload, initialized: true };
    case FETCH_CERTIFICATE_ERROR:
      console.error(payload);
      return { ...state, fetchInProgress: false, fetchError: payload, initialized: true };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

const fetchCertificateRequest = () => ({ type: FETCH_CERTIFICATE_REQUEST });
const fetchCertificateSuccess = data => ({
  type: FETCH_CERTIFICATE_SUCCESS,
  payload: data,
});
const fetchCertificateError = e => ({ type: FETCH_CERTIFICATE_ERROR, error: true, payload: e });

// ================ Thunks ================ //

export const fetchCertificate = transactionId => async dispatch => {
  dispatch(fetchCertificateRequest());
  
  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/certificate?id=${transactionId}`);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(errorData.error || 'Failed to fetch certificate');
      error.status = response.status;
      throw error;
    }
    
    const data = await response.json();
    dispatch(fetchCertificateSuccess(data));
    return data;
  } catch (e) {
    dispatch(fetchCertificateError(storableError(e)));
    throw e;
  }
};

// loadData is called before page rendering
export const loadData = (params, search, config) => dispatch => {
  const transactionId = params.id;
  
  // Skip SSR - let client handle the fetch after hydration
  // This avoids issues with server-side fetch not having access to window
  if (typeof window === 'undefined') {
    // On server, reset state but don't set fetchInProgress
    // This allows the client useEffect to trigger the fetch
    dispatch(setInitialValues({ fetchInProgress: false, fetchError: null, certificateData: null, initialized: false }));
    return Promise.resolve();
  }
  
  // On client-side navigation, fetch immediately
  return dispatch(fetchCertificate(transactionId));
};
